import React, { useState } from 'react';
import { Card, CardBody, Button, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import { homeImagesService } from '../services';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';

// Base URL for static uploads (API is at /api, uploads at /uploads)
const UPLOADS_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/api\/?$/, '');
const ORACLE_CARD_IMAGE_URL = `${UPLOADS_BASE}/uploads/static-img/Oracle%20Card.png`;
const DEEPER_ORACLE_IMAGE_URL = `${UPLOADS_BASE}/uploads/static-img/Depper%20Oracle%20Card.png`;

const HomeImagesPage: React.FC = () => {
  const [oracleCardFile, setOracleCardFile] = useState<File | null>(null);
  const [deeperOracleFile, setDeeperOracleFile] = useState<File | null>(null);
  const [uploadingOracleCard, setUploadingOracleCard] = useState(false);
  const [uploadingDeeperOracle, setUploadingDeeperOracle] = useState(false);
  const [imageKey, setImageKey] = useState(0);

  const handleUploadOracleCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oracleCardFile) {
      toast.error('Please select an image');
      return;
    }
    setUploadingOracleCard(true);
    try {
      await homeImagesService.uploadOracleCardImage(oracleCardFile);
      toast.success('Oracle Card image uploaded. It will appear in the app Home section.');
      setOracleCardFile(null);
      setImageKey((k) => k + 1);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingOracleCard(false);
    }
  };

  const handleUploadDeeperOracle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deeperOracleFile) {
      toast.error('Please select an image');
      return;
    }
    setUploadingDeeperOracle(true);
    try {
      await homeImagesService.uploadDeeperOracleReadingImage(deeperOracleFile);
      toast.success('Deeper Oracle Reading image uploaded. It will appear in the app Home section.');
      setDeeperOracleFile(null);
      setImageKey((k) => k + 1);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingDeeperOracle(false);
    }
  };

  return (
    <>
      <BreadCrumb pageTitle="Home Images" title="Home Images" />
      <Row>
        <Col md={6}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">Oracle Card (Home section)</h5>
              <p className="text-muted small mb-3">
                This image is shown in the app Home API under <code>oracleCard.imageUrl</code>.
              </p>
              <div className="mb-3">
                <Label className="d-block small text-muted mb-1">Current image</Label>
                <div className="border rounded p-2 bg-light text-center" style={{ minHeight: 120 }}>
                  <img
                    key={`oracle-${imageKey}`}
                    src={`${ORACLE_CARD_IMAGE_URL}?t=${imageKey}`}
                    alt="Oracle Card"
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
              <form onSubmit={handleUploadOracleCard}>
                <FormGroup>
                  <Label>Image (jpg, png, gif, webp – max 5MB)</Label>
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => setOracleCardFile(e.target.files?.[0] ?? null)}
                  />
                  {oracleCardFile && (
                    <small className="text-muted d-block mt-1">Selected: {oracleCardFile.name}</small>
                  )}
                </FormGroup>
                <Button color="primary" type="submit" disabled={uploadingOracleCard || !oracleCardFile}>
                  {uploadingOracleCard ? 'Uploading...' : 'Upload Oracle Card image'}
                </Button>
              </form>
            </CardBody>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">Deeper Oracle Reading (Home section)</h5>
              <p className="text-muted small mb-3">
                This image is shown in the app Home API under <code>deeperOracleReading.imageUrl</code>.
              </p>
              <div className="mb-3">
                <Label className="d-block small text-muted mb-1">Current image</Label>
                <div className="border rounded p-2 bg-light text-center" style={{ minHeight: 120 }}>
                  <img
                    key={`deeper-${imageKey}`}
                    src={`${DEEPER_ORACLE_IMAGE_URL}?t=${imageKey}`}
                    alt="Deeper Oracle Reading"
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
              <form onSubmit={handleUploadDeeperOracle}>
                <FormGroup>
                  <Label>Image (jpg, png, gif, webp – max 5MB)</Label>
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={(e) => setDeeperOracleFile(e.target.files?.[0] ?? null)}
                  />
                  {deeperOracleFile && (
                    <small className="text-muted d-block mt-1">Selected: {deeperOracleFile.name}</small>
                  )}
                </FormGroup>
                <Button color="primary" type="submit" disabled={uploadingDeeperOracle || !deeperOracleFile}>
                  {uploadingDeeperOracle ? 'Uploading...' : 'Upload Deeper Oracle Reading image'}
                </Button>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default HomeImagesPage;
